/**
 * google-drive.ts — Google Drive import via the Google Picker.
 *
 * Flow (client-side; the worker never touches Drive):
 *   1. Load Google Identity Services (token flow) + gapi Picker on demand.
 *   2. Request a `drive.file` access token (popup, first time asks consent).
 *   3. Open the Picker; the user selects one or more files.
 *   4. Caller downloads each picked file's bytes with the token and wraps them
 *      as a File, then feeds it into the EXISTING upload engine (addFiles).
 *
 * `drive.file` only grants access to files the user actually picks here, so the
 * download in fetchDriveFileAsFile() works for exactly those files.
 */

import { ACCEPTED_MIME_TYPES } from "@/lib/upload";
import {
  GOOGLE_API_KEY,
  GOOGLE_APP_ID,
  GOOGLE_CLIENT_ID,
  GOOGLE_DRIVE_SCOPE,
} from "@/lib/google-config";

export type PickedDriveFile = {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
};

export type DrivePickResult = {
  files: PickedDriveFile[];
  accessToken: string;
};

// ── Script loaders (idempotent, promise-cached) ──────────────────────────────

const scriptPromises = new Map<string, Promise<void>>();

function loadScript(src: string): Promise<void> {
  let p = scriptPromises.get(src);
  if (!p) {
    p = new Promise<void>((resolve, reject) => {
      const el = document.createElement("script");
      el.src = src;
      el.async = true;
      el.defer = true;
      el.onload = () => resolve();
      el.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(el);
    });
    scriptPromises.set(src, p);
  }
  return p;
}

let gisReady: Promise<void> | null = null;
function loadGis(): Promise<void> {
  if (!gisReady) gisReady = loadScript("https://accounts.google.com/gsi/client");
  return gisReady;
}

let pickerReady: Promise<void> | null = null;
function loadPicker(): Promise<void> {
  if (!pickerReady) {
    pickerReady = (async () => {
      await loadScript("https://apis.google.com/js/api.js");
      await new Promise<void>((resolve) =>
        (window as any).gapi.load("picker", { callback: () => resolve() }),
      );
    })();
  }
  return pickerReady;
}

// ── OAuth token (drive.file) ─────────────────────────────────────────────────

let tokenClient: any = null;

function getAccessToken(): Promise<string> {
  return new Promise((resolve, reject) => {
    const google = (window as any).google;
    if (!google?.accounts?.oauth2) {
      reject(new Error("Google sign-in failed to load"));
      return;
    }
    if (!tokenClient) {
      tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: GOOGLE_DRIVE_SCOPE,
        callback: () => {},
      });
    }
    tokenClient.callback = (resp: any) => {
      if (resp?.error) {
        reject(new Error(resp.error));
        return;
      }
      resolve(resp.access_token);
    };
    // Fired when the user closes the popup or consent fails.
    tokenClient.error_callback = (err: any) =>
      reject(new Error(err?.message || "Google sign-in was cancelled"));
    // prompt "" reuses an existing grant silently; first time shows consent.
    tokenClient.requestAccessToken({ prompt: "" });
  });
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Open the Google Picker and resolve with the selected files + the access token
 * used to download them. Resolves null if the user cancels/closes the Picker.
 */
export async function openDrivePicker(
  opts: { multiple?: boolean } = {},
): Promise<DrivePickResult | null> {
  await Promise.all([loadGis(), loadPicker()]);
  const accessToken = await getAccessToken();

  return new Promise((resolve) => {
    const g = (window as any).google;

    const view = new g.picker.DocsView(g.picker.ViewId.DOCS)
      .setMimeTypes(ACCEPTED_MIME_TYPES.join(","))
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false)
      .setMode(g.picker.DocsViewMode.LIST);

    const builder = new g.picker.PickerBuilder()
      .setDeveloperKey(GOOGLE_API_KEY)
      .setAppId(GOOGLE_APP_ID)
      .setOAuthToken(accessToken)
      .setTitle("Select files to import")
      .addView(view)
      .setCallback((data: any) => {
        const action = data[g.picker.Response.ACTION];
        if (action === g.picker.Action.PICKED) {
          const docs = data[g.picker.Response.DOCUMENTS] || [];
          const files: PickedDriveFile[] = docs.map((d: any) => ({
            id: d.id,
            name: d.name,
            mimeType: d.mimeType,
            sizeBytes: Number(d.sizeBytes || 0),
          }));
          resolve({ files, accessToken });
        } else if (action === g.picker.Action.CANCEL) {
          resolve(null);
        }
      });

    if (opts.multiple) builder.enableFeature(g.picker.Feature.MULTISELECT_ENABLED);
    builder.build().setVisible(true);
  });
}

/**
 * Download a picked Drive file's bytes and wrap them as a File, ready to hand to
 * the existing upload engine (addFiles / uploadFile). Uses the media endpoint
 * with the picker's access token — valid for exactly the files the user picked.
 */
export async function fetchDriveFileAsFile(
  picked: PickedDriveFile,
  accessToken: string,
): Promise<File> {
  const url =
    `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(picked.id)}` +
    `?alt=media&supportsAllDrives=true`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    throw new Error(`Couldn't download "${picked.name}" from Drive (${res.status})`);
  }
  const blob = await res.blob();
  return new File([blob], picked.name, {
    type: picked.mimeType || blob.type || "application/octet-stream",
  });
}
